import React from 'react';
import { motion } from 'framer-motion';
import { FaApple, FaAndroid, FaLock } from 'react-icons/fa';
import { withTranslation } from 'react-i18next';

class Pricing extends React.Component {
  constructor(props) {
    super(props);
    this.containerVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.3,
        },
      },
    };

    this.cardVariants = {
      hidden: { opacity: 0, y: 50 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          ease: 'easeOut',
          duration: 0.6,
        },
      },
    };
  }

  render() {
    const { t } = this.props
    const commonCardClasses = 'p-6 w-full';

    const renderFeatureList = (features) => (
      <ul className="space-y-3 my-6 text-gray-300">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center">
            <svg
              className="w-5 h-5 mr-2 text-green-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              ></path>
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    );

    const renderAppButtons = () => (
      <div className="flex flex-col space-y-4">
        <a
          href="https://apps.apple.com/us/app/dt-training-app/id1550242297"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-md font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
        >
          <FaApple className="w-6 h-6" />
          <span>{t('download_app_store')}</span>
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=app.dttrainingapp"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-md font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
        >
          <FaAndroid className="w-6 h-6" />
          <span>{t("get_google_play")}</span>
        </a>
      </div>
    );

    const renderTrustBadges = () => (
      <div className="mt-6 flex flex-col items-center space-y-2 text-gray-400">
        <div className="flex items-center">
          <FaLock className="w-5 h-5 mr-2" />
          <span className="text-center text-sm">{t('secure_payments')}</span>
        </div>
      </div>
    );

    return (
      <div id="pricing" className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-8 font-sans">
        <h2 className="text-3xl md:text-5xl font-extrabold text-white text-center mb-10">
          {t('choose_plan')}
        </h2>
        <motion.div
          className="bg-gray-800 rounded-lg p-6 md:p-10 max-w-4xl w-full"
          variants={this.containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row w-full">
            {/* Starter Tier */}
            <motion.div
              className={`flex-1 md:pr-4 ${commonCardClasses}`}
              variants={this.cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2 text-gray-200">{t('starter_coaching')}</h3>
                <p className="text-4xl md:text-5xl font-extrabold text-blue-500">
                  {t('price_val_one')}<span className="text-2xl text-gray-500">{t('per_month')}</span>
                </p>
                <p className="text-sm text-gray-400 mt-2">{t('save_tag_one')}</p>
              </div>
              {renderFeatureList([
                t("training_plan"),
                t('meal_plan'),
                t('in_app_support'),
                t('in_app_checkin'),
              ])}
            </motion.div>

            {/* Separator for mobile and desktop */}
            <div className="my-8 md:my-0 h-px md:h-auto w-full md:w-px bg-gray-700 md:mx-8 self-stretch relative"></div>

            {/* Elite Tier */}
            <motion.div
              className={`flex-1 relative md:pl-4 ${commonCardClasses}`}
              variants={this.cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {/* Most Value Tag */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -mt-3 bg-blue-500 text-white text-xs font-bold py-1 px-3 rounded-full uppercase whitespace-nowrap z-10">
                {t('most_value')}
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2 text-gray-200">{t('elite_coaching')}</h3>
                <p className="text-4xl md:text-5xl font-extrabold text-blue-500">
                  {t('price_val_two')}<span className="text-2xl text-gray-500">{t('per_month')}</span>
                </p>
                <p className="text-sm text-gray-400 mt-2">{t('save_tag_two')}</p>
              </div>
              {renderFeatureList([
                t('everything_starter'),
                t('zoom_call'),
                t('24h_support'),
                t('extra_resources'),
              ])}
              
              {/* Scarcity Line Added Here */}
              <div className="mt-6 py-2 px-4 border border-blue-500/30 bg-blue-500/10 rounded-lg">
                <p className="text-xs md:text-sm text-blue-300 font-medium text-center">
                  {t('limited_clients')}
                </p>
              </div>
            </motion.div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-700 text-center">
            <p className="text-gray-300 mb-4 text-sm font-semibold uppercase tracking-wide">
              {t('continue_on_app')}
            </p>
            {renderAppButtons()}
            {renderTrustBadges()}
          </div>
        </motion.div>
      </div>
    );
  }
}

export default withTranslation()(Pricing);